import { useState, useEffect } from "react";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { Container } from "@shared/components/Container";
import { ProductCard, SeoHead } from "@shared/components";
import { PaginationComponent } from "@shared/components/ui/Pagination";
import { slugToReadableName } from "@shared/utils";
import { useProductsByCategory } from "@shared/hooks/useProducts";
import { useCategoryBySlug } from "@shared/hooks/useCategories";
import { useHomeData } from "@shared/hooks/useHome";
import { generateCollectionPageSchema, generateBreadcrumbSchema } from "@shared/utils/schemaOrg";
import type { Product } from "@shared/types/product";
import type { NavbarSubcategory } from "@shared/types/homepage";
// Removido useCart - ahora usamos directamente el store de Zustand

export function Products() {
  const { slugCatalogo, categoriaSlug } = useParams<{
    slugCatalogo?: string;
    categoriaSlug: string;
  }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Obtener datos del estado (subcategorías y subcategoría seleccionada por defecto)
  const stateSubcategories = location.state?.subcategories as NavbarSubcategory[] | undefined;
  const stateSelectedSubcategoryId = location.state?.selectedSubcategoryId as string | null | undefined;

  // Estado para la subcategoría seleccionada (usa query param o estado por defecto)
  const subcategoryIdFromQuery = searchParams.get("subcategoria");
  const hasSubcategoryInQuery = searchParams.has("subcategoria"); // Verificar si existe el parámetro (incluso si es "all")
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(
    subcategoryIdFromQuery === "all" ? null : (subcategoryIdFromQuery || stateSelectedSubcategoryId || null)
  );

  // Paginación: obtener página actual desde query params o usar 1 por defecto
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = 12; // Productos por página

  // 1. Obtener categoría por slug
  const {
    data: category,
    isLoading: categoryLoading,
    error: categoryError,
  } = useCategoryBySlug(categoriaSlug || "");

  // 2. Obtener datos del navbar para verificar subcategorías
  const { data: homeData } = useHomeData();
  const navbarCatalog = homeData?.navbar?.catalogs?.find(
    (cat) => cat.slug === slugCatalogo
  );
  const navbarCategory = navbarCatalog?.categories?.find(
    (cat) => cat.id === category?.id || cat.slug === category?.slug
  );
  
  // Usar subcategorías del estado o del navbar
  const subcategories = stateSubcategories || navbarCategory?.subcategories || [];
  const hasSubcategories = subcategories.length > 0;

  // 3. Si hay subcategorías y no hay una seleccionada (ni en query params ni en state), usar la primera por defecto
  // Solo ejecutar si NO hay una selección explícita en los query params (incluso si es "all")
  useEffect(() => {
    if (
      hasSubcategories && 
      !hasSubcategoryInQuery && // No hay parámetro "subcategoria" en la URL (primera carga)
      !stateSelectedSubcategoryId && // No viene del estado de navegación
      subcategories.length > 0
    ) {
      const firstSubcategoryId = subcategories[0].id;
      setSelectedSubcategoryId(firstSubcategoryId);
      // Mantener la página actual si existe
      const params = new URLSearchParams(searchParams);
      params.set("subcategoria", firstSubcategoryId);
      if (!params.has("page")) {
        params.set("page", "1");
      }
      setSearchParams(params, { replace: true });
    }
  }, [hasSubcategories, hasSubcategoryInQuery, stateSelectedSubcategoryId, subcategories, searchParams, setSearchParams]);

  // Sincronizar selectedSubcategoryId con los query params cuando cambian (navegación hacia atrás/adelante)
  useEffect(() => {
    if (hasSubcategoryInQuery) {
      const subcategoryId = subcategoryIdFromQuery === "all" ? null : subcategoryIdFromQuery;
      setSelectedSubcategoryId(subcategoryId);
    }
  }, [subcategoryIdFromQuery, hasSubcategoryInQuery]);

  // 4. Determinar qué categoryId usar para buscar productos
  // Si hay subcategorías Y hay una subcategoría seleccionada, usar la subcategoría
  // Si hay subcategorías pero NO hay una seleccionada (o se selecciona "Todos"), usar la categoría root
  // Si NO hay subcategorías, usar la categoría root directamente
  const categoryIdForProducts = hasSubcategories && selectedSubcategoryId
    ? selectedSubcategoryId
    : category?.id || "";

  // 5. Obtener productos por categoryId con paginación
  const {
    data: productsData,
    isLoading: productsLoading,
    error: productsError,
  } = useProductsByCategory(
    categoryIdForProducts,
    currentPage,
    pageSize
  );

  // 6. Manejar cambio de subcategoría (resetear a página 1 cuando cambia la subcategoría)
  const handleSubcategoryChange = (subcategoryId: string | null) => {
    setSelectedSubcategoryId(subcategoryId);
    const params = new URLSearchParams();
    if (subcategoryId) {
      // Si hay una subcategoría específica, guardarla
      params.set("subcategoria", subcategoryId);
    } else {
      // Si se selecciona "Todos" (null), agregar parámetro "all" para indicar selección explícita
      params.set("subcategoria", "all");
    }
    params.set("page", "1"); // Resetear a página 1 al cambiar subcategoría
    setSearchParams(params);
  };

  // 7. Manejar cambio de página
  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", page.toString());
    setSearchParams(params);
    // Scroll hacia arriba para mejor UX
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Loading y nombre de categoría
  const isLoading = categoryLoading || productsLoading;
  const error = categoryError || productsError;
  const categoryName = category?.name || slugToReadableName(categoriaSlug || "");
  const selectedSubcategory = subcategories.find((sub) => sub.id === selectedSubcategoryId);

  // Datos de paginación del backend
  const paginationMeta = productsData?.pagination;
  const totalPages = paginationMeta?.totalPages ?? 1;
  const totalItems = paginationMeta?.total ?? 0;
  const activePage = paginationMeta?.page ?? currentPage;

  // Log para ver qué llega del backend
  console.log("📦 Products Page - Backend Data:", {
    // ==========================================
    // 🎯 DATOS QUE VIENEN DEL BACKEND
    // ==========================================
    backend: {
      // Categoría obtenida del backend (GET /category/slug/:slug)
      category: category ? {
        id: category.id,
        name: category.name,
        slug: category.slug,
        parentId: category.parentId,
        isRootCategory: category.isRootCategory,
        hasParent: category.hasParent,
        catalogId: category.catalogId,
      } : null,
      
      // Productos obtenidos del backend (GET /product/category/:categoryId)
      productsResponse: productsData ? {
        success: productsData.success,
        data: productsData.data?.map((product) => ({
          id: product.id,
          name: product.name,
          slug: product.slug,
          categoryId: product.categoryId,
          brand: product.brand,
          active: product.active,
          hasImages: product.hasImages,
          imageCount: product.imageCount,
        })),
        productsCount: productsData.data?.length || 0,
        pagination: productsData.pagination,
        // Respuesta completa del backend (sin procesar)
        rawResponse: productsData,
      } : null,
      
      // Subcategorías obtenidas del navbar (GET /homepage)
      subcategoriesFromNavbar: navbarCategory?.subcategories?.map((sub) => ({
        id: sub.id,
        name: sub.name,
        slug: sub.slug,
      })),
    },
    
    // ==========================================
    // 🧮 DATOS CALCULADOS EN EL FRONTEND
    // ==========================================
    frontend: {
      // Parámetros de la URL (React Router)
      urlParams: {
        slugCatalogo,
        categoriaSlug,
        subcategoryIdFromQuery,
      },
      
      // Estados calculados
      computed: {
        hasSubcategories,
        selectedSubcategoryId,
        selectedSubcategory: selectedSubcategory ? {
          id: selectedSubcategory.id,
          name: selectedSubcategory.name,
          slug: selectedSubcategory.slug,
        } : null,
        categoryIdForProducts,
      },
      
      // Estados de carga y errores (React Query)
      loadingStates: {
        categoryLoading,
        productsLoading,
        total: isLoading,
      },
      errorStates: {
        categoryError: categoryError?.message,
        productsError: productsError?.message,
        total: error?.message,
      },
      
      // Datos del estado de navegación (React Router state)
      navigationState: {
        stateSubcategories: stateSubcategories?.map((sub) => ({
          id: sub.id,
          name: sub.name,
          slug: sub.slug,
        })),
        stateSelectedSubcategoryId,
      },
    },
  });

  // Generar schemas para SEO
  const pageTitle = selectedSubcategory 
    ? `${selectedSubcategory.name} - ${categoryName}`
    : categoryName;
  const pageDescription = category?.seoDescription || 
    `Explora nuestra selección de ${pageTitle.toLowerCase()} en Cerámicas Gassull. Productos de alta calidad para tu proyecto de construcción.`;
  const canonicalUrl = selectedSubcategory
    ? `/catalog/${slugCatalogo}/categoria/${categoriaSlug}/subcategoria/${selectedSubcategory.slug}`
    : `/catalog/${slugCatalogo}/categoria/${categoriaSlug}`;
  
  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    ...(category ? [{ name: category.name, url: `/catalog/${slugCatalogo}/categoria/${category.slug}` }] : []),
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);
  const collectionSchema = generateCollectionPageSchema(
    pageTitle,
    pageDescription,
    canonicalUrl,
    productsData?.data
  );

  return (
    <>
      <SeoHead
        category={category || undefined}
        title={pageTitle}
        description={pageDescription}
        keywords={category?.seoKeywords || undefined}
        canonical={canonicalUrl}
        schemaJsonLd={[breadcrumbSchema, collectionSchema]}
      />
      <div className="min-h-screen">
      {/* Header de la página */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {categoryName}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explora nuestros productos disponibles
            </p>
          </div>
        </div>
      </section>

      {/* Contenido principal */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="h-48 bg-gray-200 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : error ? (
            <Container variant="default" padding="lg" className="text-center">
              <div className="text-red-600">
                <p>Error al cargar los datos: {error.message}</p>
              </div>
            </Container>
          ) : !category ? (
            <Container variant="default" padding="lg" className="text-center">
              <p className="text-gray-600">
                Categoría no encontrada.
              </p>
            </Container>
          ) : (
            <div className="space-y-6">
              {/* Filtro de subcategorías (solo si hay subcategorías) */}
              {hasSubcategories && subcategories.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        Filtrar por subcategoría:
                      </h2>
                      <div className="flex flex-wrap gap-3">
                        {subcategories.map((subcategory) => (
                          <label
                            key={subcategory.id}
                            className="flex items-center cursor-pointer group"
                          >
                            <input
                              type="radio"
                              name="subcategory"
                              value={subcategory.id}
                              checked={selectedSubcategoryId === subcategory.id}
                              onChange={() => handleSubcategoryChange(subcategory.id)}
                              className="sr-only"
                            />
                            <span
                              className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                                selectedSubcategoryId === subcategory.id
                                  ? "bg-green-600 text-white border-green-600 font-medium shadow-md"
                                  : "bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:bg-green-50"
                              }`}
                            >
                              {subcategory.name}
                            </span>
                          </label>
                        ))}
                        {/* Opción para ver todos los productos de la categoría (sin filtrar por subcategoría) */}
                        {hasSubcategories && (
                          <label className="flex items-center cursor-pointer group">
                            <input
                              type="radio"
                              name="subcategory"
                              value="all"
                              checked={selectedSubcategoryId === null}
                              onChange={() => handleSubcategoryChange(null)}
                              className="sr-only"
                            />
                            <span
                              className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                                selectedSubcategoryId === null
                                  ? "bg-gray-600 text-white border-gray-600 font-medium shadow-md"
                                  : "bg-white text-gray-700 border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                              }`}
                            >
                              Todos ({categoryName})
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Información de productos */}
              {productsData?.data && productsData.data.length > 0 ? (
                <div className="space-y-4">
                  {/* Grid de productos - 2 columnas móvil, 3 tablet, 4 desktop */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 auto-rows-fr">
                    {productsData.data.map((product: Product) => (
                      <div key={product.id} className="w-full">
                        <ProductCard
                          product={product}
                          variant="modern"
                          showQuantitySelector={false}
                          className="w-full"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Controles de paginación */}
                  {totalPages > 1 && (
                    <PaginationComponent
                      currentPage={activePage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                    />
                  )}
                </div>
              ) : (
                <Container variant="default" padding="lg" className="text-center">
                  <p className="text-gray-600">
                    No hay productos disponibles
                    {selectedSubcategory ? ` en "${selectedSubcategory.name}"` : " en esta categoría"}.
                  </p>
                </Container>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
    </>
  );
}
